import { isPlatformBrowser, Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import { SplashModalGateService } from '@app/core/services/splash-modal-gate.service';
import { FileDropEntry } from '@app/shared/components/smash-file-drop/file-drop-entry';
import { FileSystemMetadata } from '@app/shared/components/smash-file-drop/interfaces';
import { isSafari } from '@app/shared/helpers/environment';
import { Identity } from '@app/shared/models/identity';
import { DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { filter, first, take } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromRoot from '@app/reducers/index';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { RawFileError } from '@app/client/uploader/interfaces/file';
import { DropboxActions, LayoutActions, UploadActions, WorkflowActions } from '@app/client/uploader/store/actions';
import { ThemeSimplified } from '@app/shared/helpers/theme';
import { ErrorDetails } from '@app/shared/helpers/uploader';
import { ModalFatalUploadErrorComponent } from '../send/modals/modal-fatal-upload-error/modal-fatal-upload-error.component';


@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit, OnDestroy {

  selectedLanguage$: Observable<any>;
  menuDisplayed$: Observable<boolean>;
  domain$: Observable<GetRecordOutput['record']>;
  rawFiles$: Observable<FileSystemMetadata[]>;
  rawFilesError$: Observable<RawFileError>;
  isFileOver$: Observable<boolean>;
  identity$: Observable<Identity>;
  dropboxCreating$: Observable<boolean>;
  dropboxError$: Observable<any>;
  dropboxCreating = false;
  uploaderMode$: Observable<GetRecordOutput['record']['options']['transferMode']['default']>;
  theme$: Observable<ThemeSimplified>;
  // Blocks file drop/selection until the discover-premium splash modal flow has resolved for this
  // visit (won't open, or dismissed via one of its two CTAs) -- see SplashModalGateService.
  fileSelectionBlocked$: Observable<boolean>;
  currentTooltipState: 'in' | 'out' = 'in';
  subscriptions: Subscription[] = [];
  isSafari: boolean;
  longpress: any;

  tooltipAnimate: NodeJS.Timeout;

  isOnDropboxRoute = false;

  constructor(
    private store$: Store<fromUploader.State & fromRoot.State>,
    private router: Router,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private location: Location,
    private rawFileStoreService: RawFileStoreService,
    private readonly splashModalGate: SplashModalGateService,
    private dialog: DialogService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.fileSelectionBlocked$ = this.splashModalGate.fileSelectionBlocked$;
    this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
    this.selectedLanguage$ = this.store$.select(fromRoot.getSelectedLanguage);
    this.rawFiles$ = this.store$.select(fromUploader.getAllRawFiles);
    this.isFileOver$ = this.store$.select(fromUploader.getIsFileOver);
    this.rawFilesError$ = this.store$.select(fromUploader.getRawFilesError);
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
    this.uploaderMode$ = this.store$.select(fromUploader.getMode);
    this.dropboxCreating$ = this.store$.select(fromUploader.getDropboxCreating);
    this.dropboxError$ = this.store$.select(fromUploader.getDropboxError);
    this.theme$ = this.store$.select(fromRoot.getTheme);
  }


  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.store$.dispatch(UploadActions.ResetUpload());
      this.store$.dispatch(DropboxActions.ResetDropbox());
      this.isSafari = isSafari();
      this.startIntervalTooltipInfos();

      // TO DO find a better way ... because it catch routes for viewer :id
      if (this.route.snapshot.paramMap.get('dropboxId')) {
        this.isOnDropboxRoute = true;
        this.getDropbox();
      } else {
        this.redirectLangUrl();
      }

      // Surface a failed team-dropbox creation (e.g. the caller lacks the permission to
      // create one -- ForbiddenError) via the shared fatal-error modal. Without this,
      // DropboxEffects#createTeamDropbox$ still dispatches CreateTeamDropboxFailure and the
      // reducer still stops dropboxCreating (the spinner correctly disappears), but
      // dropboxError was never read anywhere: the user was left with a flashed spinner and
      // no explanation at all.
      this.subscriptions.push(
        this.dropboxError$.pipe(filter(error => error !== null)).subscribe(error => {
          this.openModalUploadFatalError({
            errorName: error?.name,
            errorMessage: error?.message,
            errorId: error?.errorId,
            errorDetails: error?.details,
          });
        }),
      );
    }
  }

  openModalUploadFatalError({ errorName, errorMessage, errorId, errorDetails }: { errorName?: string, errorMessage?: string, errorId?: string; errorDetails?: ErrorDetails }): void {
    this.dialog.open(ModalFatalUploadErrorComponent,
      {
        closeButton: false,
        enableClose: false,
        size: 'xs',
        data: {
          errorName,
          errorMessage,
          errorId,
          errorDetails,
        },
      });
  }

  private startIntervalTooltipInfos(): void {
    this.tooltipAnimate = setInterval(() => {
      this.currentTooltipState = this.currentTooltipState === 'in' ? 'out' : 'in';
    }, 3000);
  }

  getDropbox(): void {
    const id = this.route.snapshot.paramMap.get('dropboxId');
    this.store$.dispatch(DropboxActions.GetDropboxPreview({ dropboxId: id }));
  }

  redirectLangUrl() {
    const userLang = this.translateService.currentLang;
    if (userLang === Constant.languageFallback) {
      return;
    }

    this.location.go(userLang);
  }

  ngOnDestroy(): void {
    clearInterval(this.tooltipAnimate);
    this.subscriptions.map(subscription => subscription.unsubscribe());
  }

  fileOver() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: true }));
  }

  onLongpressChanged(event: any) {
    this.longpress = event;
  }

  fileLeave() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: false }));
  }

  // Set by (entryReadError), captured+reset synchronously at the top of dropped() -- setFiles()'s
  // own resetErrors() would otherwise wipe an error dispatched any earlier, and reading this flag
  // only AFTER awaiting setFiles() would risk a second, overlapping drop's entryReadError
  // overwriting it before this drop's check runs. See onEntryReadError().
  private hadEntryReadError = false;

  onEntryReadError(): void {
    this.hadEntryReadError = true;
  }

  async dropped(files: FileDropEntry[]) {
    const hadEntryReadError = this.hadEntryReadError;
    this.hadEntryReadError = false;
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    await this.rawFileStoreService.setFiles(files, identity);
    if (hadEntryReadError) {
      this.rawFileStoreService.reportRelativePathTooLongError();
    }
    this.subscriptions.push(this.rawFiles$.subscribe(rawFiles => {
      if (rawFiles.length) {
        this.router.navigate(['prepare'], { relativeTo: this.route });
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'PREPARE' }));
      }
    }));
  }

  async onSpinnerClick() {
    const uploaderMode$ = await this.uploaderMode$.pipe(first(mode => !!mode)).toPromise();
    if (uploaderMode$ === 'Dropbox') {
      await this.createTeamDropbox();
    }
    return;
  }

  async createTeamDropbox() {
    const domain = await lastValueFrom(this.domain$.pipe(take(1)));
    const dropboxCreating = await lastValueFrom(this.dropboxCreating$.pipe(take(1)));
    if (!dropboxCreating) {
      this.store$.dispatch(DropboxActions.CreateTeamDropbox({ teamId: domain.team }));
      const dropboxCreated = await this.store$.select(fromUploader.getDropboxCreated).pipe(first(dropboxCreated => !!dropboxCreated)).toPromise();
      if (dropboxCreated) {
        this.router.navigate(['request-created'], { relativeTo: this.route });
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'REQUEST_CREATED' }));
      }
    }
  }
}
